import { ApolloClient } from "@apollo/client";
import { ConfigGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/ConfigGraphQlAdapter";
import {
  GET_CONFIG_DEDUCTION_SLA_CONFIGS,
  UPDATE_CONFIG_DEDUCTION_SLA_CONFIG,
} from "@src/domain/graphql/config";
import {
  ConfigDeductionSlaConfig,
  type IConfigDeductionSlaConfig,
} from "@src/domain/entities/config";
import type {
  GetConfigDeductionSlaConfigsDTO,
  UpdateConfigDeductionSlaConfigDTO,
} from "@src/domain/dto/config";
import type {
  ConfigDeductionSlaConfigsListResult,
  IConfigDeductionSlaConfigRepository,
} from "@src/domain/repositories/config";
import {
  requireConfigMutationData,
  throwIfConfigGraphQLError,
  throwIfConfigMutationError,
  toConfigListResult,
} from "./configRepository.shared";

function mapSlaConfigItem(item: IConfigDeductionSlaConfig): ConfigDeductionSlaConfig {
  return ConfigDeductionSlaConfig.from({
    id: item.id,
    configKey: item.configKey,
    configValue: item.configValue,
    description: item.description ?? null,
  });
}

export class ConfigDeductionSlaConfigRepository
  implements IConfigDeductionSlaConfigRepository
{
  private readonly tenantClient: ApolloClient;

  constructor(
    tenantClient: ApolloClient = ConfigGraphQlAdapter.getInstance().getClient(),
  ) {
    this.tenantClient = tenantClient;
  }

  async getDeductionSlaConfigs(
    params: GetConfigDeductionSlaConfigsDTO,
  ): Promise<ConfigDeductionSlaConfigsListResult> {
    const { data, error } = await this.tenantClient.query<{
      configDeductionSlaConfigs: {
        items: IConfigDeductionSlaConfig[];
        total: number;
        page: number;
        limit: number;
      };
    }>({
      query: GET_CONFIG_DEDUCTION_SLA_CONFIGS,
      variables: {
        params: {
          search: params.search ?? null,
          page_number: params.page_number,
          page_size: params.page_size,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfConfigGraphQLError({ error });

    const response = data?.configDeductionSlaConfigs;
    if (!response) {
      throw new Error("Failed to fetch deduction SLA configs");
    }

    return toConfigListResult(
      response,
      mapSlaConfigItem,
      params.page_number,
      params.page_size,
    );
  }

  async updateDeductionSlaConfig(
    params: UpdateConfigDeductionSlaConfigDTO,
  ): Promise<ConfigDeductionSlaConfig> {
    const result = await this.tenantClient.mutate<{
      updateConfigDeductionSlaConfig: IConfigDeductionSlaConfig;
    }>({
      mutation: UPDATE_CONFIG_DEDUCTION_SLA_CONFIG,
      variables: {
        params: {
          id: params.id,
          configValue: params.configValue,
          description: params.description,
        },
      },
      errorPolicy: "all",
    });

    throwIfConfigMutationError(result.error);
    const payload = requireConfigMutationData(
      result.data?.updateConfigDeductionSlaConfig,
      result.error,
      "Failed to update deduction SLA config",
    );
    return mapSlaConfigItem(payload);
  }
}
